// Admin-created public tests (PYPs and general public tests).
// See docs/FeatureDesigns/assignments-tests/admin-public-tests.md
import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QuestionPicker } from '@shared-ui/question-picker';
import type { QuestionSet, QuestionSetSection, ExamPaperRef, QuestionSetPurpose, QuestionSetModality, WorkingChoice } from '@shared-ui/question-set-types';
import { WORKING_CHOICES, WORKING_HELP } from '@shared-ui/question-set-types';
import { QUESTION_SUBJECT_OPTIONS } from '@padcanvas/shared';
import { MARKING_PRESETS, type MarkingScheme, resolveHintLevelSetting, type HintLevelSetting } from '@padcanvas/shared';
import { kindOf, QUESTION_SET_KINDS, QUESTION_SET_KIND_LABELS, type QuestionSetKind } from '@padcanvas/shared';
import { TestLibrary, testLibraryQueryParams, DEFAULT_TEST_LIBRARY_PAGE_SIZE } from '@shared-ui/test-library';
import { isDevEnvironment } from '@shared-ui/environment';
import { questionSetApi } from '../../api/questionSetApi';
import { adminApi } from '../../api/adminApi';
import { TopicSelector } from '@shared-ui/components/TopicSelector';
import type { Module } from '../../types';
import { InfoTip, Modal, IntegerInput, DatePicker, BackButton, MarkingSelect, type AiInstructionsValue } from '@shared-ui/components';
import { useTestQuestionsExport } from './useTestQuestionsExport';
import { TestImportDialog } from './TestImportDialog';
import { TestDetailView } from './TestDetailView';
import { downloadJson } from '../../utils/downloadJson';
import './Tests.css';

// Slug for export filenames: "CBSE Class 10 Maths" → "cbse-class-10-maths".
function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'test';
}

// Display-only: capitalize a subject label ("physics" → "Physics"). Values stay lowercase.
function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

const EXAM_OPTIONS = ['JEE', 'NEET', 'CBSE', 'ICSE', 'STATE'] as const;
const GRADE_OPTIONS = ['7', '8', '9', '10', '11', '12'] as const;
const SUBJECT_OPTIONS = ['maths', 'physics', 'chemistry', 'biology'] as const;

// Canonical, derived displayString — MUST mirror backend src/shared/pypDisplayString.ts.
//   JEE/NEET:  "JEE · 2021-02-24 · Shift 1 · Physics"
//   CBSE/ICSE: "CBSE · 2024 · Class 12 · Physics · Set 55/2/3"
function buildDisplayString(ref: Partial<ExamPaperRef>): string {
  if (!ref.exam) return '';
  const SEP = ' · ';
  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const subj = (ref.subjects ?? []).map(cap).join(', ');
  const shift = (s?: string) => {
    if (!s) return '';
    const m = s.match(/(?:shift|s)\s*[-\s]?\s*([12])\b/i) || s.match(/\b([12])\s*$/);
    return m ? `Shift ${m[1]}` : s.trim();
  };
  const setCode = (s?: string) => { if (!s) return ''; const c = s.replace(/^set\s*/i, '').trim(); return c ? `Set ${c}` : ''; };
  const ex = ref.exam.toUpperCase();
  if (ex === 'JEE' || ex === 'NEET') return [ref.exam, ref.date, shift(ref.session), subj].filter(Boolean).join(SEP);
  if (ex === 'CBSE' || ex === 'ICSE') return [ref.exam, (ref.date ?? '').slice(0, 4), ref.class ? `Class ${ref.class}` : '', subj, setCode(ref.session)].filter(Boolean).join(SEP);
  return [ref.exam, ref.date, ref.session, subj].filter(Boolean).join(SEP);
}

/** Phase 2 section editor (UI model). Questions are partitioned in order by
 *  `size`; `requiredCount` is the "attempt any K" (null ⇒ all required). */
interface SectionForm {
  id: string;
  title: string;
  size: number;
  requiredCount: number | null;
  /** Tier 2: section marking override; undefined ⇒ inherit the test default. */
  marking?: MarkingScheme;
}

/** The editable play knobs (mirror of backend QuestionSetConfig, minus timer/navigation).
 *  Timer is the separate Time-limit field; practice is always untimed. */
interface AttemptKnobs {
  defaultHintLevel: HintLevelSetting;
  showFeedbackImmediately: boolean;
  allowMultipleAttempts: boolean;
  allowAnalysisBeforeSubmit: boolean;
  shuffleQuestions: boolean;
  /** Mixed mode: the working is done on paper and only the answer is recorded
   *  here. Shown read-only as part of the preset — it is what the mode IS, not a
   *  knob to nudge. */
}

// Mirror of backend MODALITY_PRESETS (functions/src/questionSet/modality.ts) — used to SHOW the
// preset values read-only. Keep in sync. See qset-model-target.md §4 (preset-OR-custom).
const MODE_PRESET: Record<'practice' | 'test', AttemptKnobs> = {
  practice: { defaultHintLevel: 'guided', showFeedbackImmediately: true, allowMultipleAttempts: true, allowAnalysisBeforeSubmit: true, shuffleQuestions: false },
  test: { defaultHintLevel: 'none', showFeedbackImmediately: false, allowMultipleAttempts: false, allowAnalysisBeforeSubmit: false, shuffleQuestions: false },
};


/**
 * What picking a kind SETS. The inverse of `kindOf`, for writing rather than reading.
 *
 * `kindQuery` in shared is the inverse for *querying* and answers `not-FullPYP`,
 * which is a predicate and not a value anything can be stored as — so the write
 * side needs its own table. Kept next to the picker that uses it.
 *
 * `playMode` rides along because the natural default differs: a paper is sat as
 * a test, a chapter exercise is practised.
 */
const KIND_FORM: Record<QuestionSetKind, {
  purpose: QuestionSetPurpose;
  isPYQ: boolean;
  isPyp: boolean;
  playMode: 'practice' | 'test';
}> = {
  'past-paper': { purpose: 'exam-paper', isPYQ: true, isPyp: true, playMode: 'test' },
  'mock-test': { purpose: 'exam-paper', isPYQ: false, isPyp: false, playMode: 'test' },
  'topicwise-pyq': { purpose: 'topic-exercise', isPYQ: true, isPyp: false, playMode: 'practice' },
  'topic-exercise': { purpose: 'topic-exercise', isPYQ: false, isPyp: false, playMode: 'practice' },
};

/** What each kind IS, in the one line a picker can carry. */
const KIND_HINT: Record<QuestionSetKind, string> = {
  'past-paper': 'A real exam paper, whole and as it appeared.',
  'mock-test': 'A full-length paper we set, not a real one.',
  'topicwise-pyq': 'One chapter’s worth of real exam questions.',
  'topic-exercise': 'Questions for one chapter, ours.',
};

interface FormState {
  title: string;
  description: string;
  source: string;
  classes: string[];
  targetExam: string;
  targetExams: string[];   // topic exercises: multi-exam targeting (empty = all exams)
  timeLimitMinutes: string;
  // ── Purpose / topic-exercise (P1) — see qset-model-target.md ──
  // 'exam-paper' is the existing flow (PYP toggle below); 'topic-exercise' shows
  // the anchor + difficulty + modality fields and hides the PYP block.
  purpose: QuestionSetPurpose;
  anchorSubject: string;         // drives the module picker; not sent (server derives subjects)
  anchorModuleId: string;        // the chapter/topic this exercise is anchored to
  difficulty: '' | 'easy' | 'medium' | 'hard';
  // ── How it plays: a PRESET (Practice/Test → knobs read-only) OR Custom (knobs editable). §12 ──
  playMode: 'practice' | 'test' | 'custom';
  allowSwitch: boolean;          // presets only: offer BOTH Practice & Test (student chooses at launch)
  custom: AttemptKnobs;          // used when playMode === 'custom'
  /** Display flag (not a play knob): show per-question marks to the student.
   *  Orthogonal to play mode — a CBSE "test" shows marks, a JEE "test" doesn't —
   *  so it stays editable regardless of preset/custom. See docs/FeatureDesigns/assignments-tests/questionset-show-marks.md. */
  showMarks: boolean;
  /** Which exam's CBT screen a test sitting replicates. `'auto'` is the default
   *  and means "derive it" — JEE past papers, mocks and topicwise PYQ get the
   *  JEE Main screen without anyone choosing. See exam-interface-target.md. */
  examInterface: 'auto' | 'jee-main' | 'jee-advanced' | 'none';
  /** Whether the working is marked, as the students were told. `'default'`
   *  leaves it to the tenant setting. Like `examInterface`, orthogonal to
   *  preset/custom. See working-role-target.md §2. */
  working: WorkingChoice;
  // PYQ assertion (§3f). isPYQ = "previous-year questions"; isPyp = "...and it's the complete
  // paper" (FullPYP, requires pypRef). Mapped to pyqType on save: full→FullPYP, PYQ→MixedPYP, else None.
  isPYQ: boolean;
  isPyp: boolean;
  pyp: {
    exam: string;
    class: string;
    subjects: string[];
    date: string;
    session: string;
    displayString: string;  // derived from the fields above (read-only)
  };
  questionIds: string[];
  sectionsEnabled: boolean;
  sections: SectionForm[];
  /** Tier 2: test-level default marking rule. */
  marking?: MarkingScheme;
  /** Free-text AI steering for this set, layered after the teacher's own
   *  instructions at prompt time. See assignment-level-instructions.md. */
  aiInstructions: AiInstructionsValue;
}

/** Build the wire `sections[]` by slicing `questionIds` in order by each
 *  section's `size`. Returns undefined when sections aren't in use. */
function buildSections(form: FormState): QuestionSetSection[] | undefined {
  if (!form.sectionsEnabled || form.sections.length === 0) return undefined;
  let offset = 0;
  return form.sections.map(s => {
    const questionIds = form.questionIds.slice(offset, offset + s.size);
    offset += s.size;
    return {
      id: s.id,
      ...(s.title.trim() ? { title: s.title.trim() } : {}),
      questionIds,
      ...(s.requiredCount != null && s.requiredCount < s.size ? { requiredCount: s.requiredCount } : {}),
      ...(s.marking ? { marking: s.marking } : {}),
    };
  });
}

/** Validation messages for the section editor (empty ⇒ ok). */
function validateSectionForm(form: FormState): string[] {
  if (!form.sectionsEnabled) return [];
  const errs: string[] = [];
  const total = form.sections.reduce((sum, s) => sum + s.size, 0);
  if (form.sections.length === 0) errs.push('Add at least one section, or turn sections off.');
  if (total !== form.questionIds.length) {
    errs.push(`Section sizes sum to ${total} but the test has ${form.questionIds.length} questions.`);
  }
  form.sections.forEach((s, i) => {
    if (s.size < 1) errs.push(`Section ${i + 1}: size must be ≥ 1.`);
    if (s.requiredCount != null && (s.requiredCount < 1 || s.requiredCount > s.size)) {
      errs.push(`Section ${i + 1}: "attempt any" must be between 1 and ${s.size}.`);
    }
  });
  return errs;
}

function emptyForm(): FormState {
  return {
    title: '',
    description: '',
    source: '',
    classes: [],
    targetExam: '',
    targetExams: [],
    timeLimitMinutes: '',
    purpose: 'exam-paper',
    anchorSubject: '',
    anchorModuleId: '',
    difficulty: '',
    playMode: 'test',          // exam-paper is the default purpose → opens as a test
    allowSwitch: false,
    custom: { ...MODE_PRESET.test },
    showMarks: false,          // opt-in; off for practice and JEE-style papers
    examInterface: 'auto',     // derived: a JEE paper gets the JEE Main screen
    working: 'default',        // the tenant's default per modality and question type
    // Mixed mode's one editable knob. Defaults to what the chosen preset says,
    // and a teacher turns it off for a short objective test where there is no
    // working worth collecting.

    isPYQ: false,
    isPyp: false,
    pyp: { exam: '', class: '', subjects: [], date: '', session: '', displayString: '' },
    questionIds: [],
    sectionsEnabled: false,
    sections: [],
    aiInstructions: {},
  };
}

function fromQuestionSet(qs: QuestionSet): FormState {
  return {
    title: qs.title,
    description: qs.description ?? '',
    source: qs.source ?? '',
    classes: qs.classes ?? [],
    targetExam: qs.targetExam ?? '',
    targetExams: qs.targetExams ?? [],
    timeLimitMinutes: qs.config.timeLimitSeconds ? String(Math.round(qs.config.timeLimitSeconds / 60)) : '',
    // Purpose: explicit when set, else derive from the legacy shape (PYP/test → exam-paper).
    purpose: qs.purpose ?? 'exam-paper',
    anchorSubject: qs.subjects?.[0] ?? '',
    // The picker still calls it the anchor; the stored field is `topics`, which
    // is where `anchorModuleId` retired. See qset-model-target.md §Module association.
    anchorModuleId: qs.topics?.[0] ?? '',
    difficulty: (qs.difficulty === 'easy' || qs.difficulty === 'medium' || qs.difficulty === 'hard') ? qs.difficulty : '',
    // Preset set (has allowedModalities) → playMode = its default; else Custom (full stored config).
    playMode: qs.allowedModalities?.length ? (qs.defaultModality ?? qs.allowedModalities[0]) : 'custom',
    allowSwitch: (qs.allowedModalities?.length ?? 0) > 1,
    // Defaults must match what the rest of the product does with an absent knob,
    // or the form shows a box unticked for a set that in fact allows the thing —
    // and writes that false back on save. `allowAnalysisBeforeSubmit` is `?? true`
    // at its only runtime reader (student-app useSessionSource), and
    // `allowMultipleAttempts` is `?? true` on the teacher path that actually cuts
    // the grants. Every stored set now carries both explicitly (backfill 007), so
    // these fire only for a set written by something that skipped them.
    custom: {
      defaultHintLevel: resolveHintLevelSetting(qs.config?.defaultHintLevel),
      showFeedbackImmediately: qs.config?.showFeedbackImmediately ?? false,
      allowMultipleAttempts: qs.config?.allowMultipleAttempts ?? true,
      allowAnalysisBeforeSubmit: qs.config?.allowAnalysisBeforeSubmit ?? true,
      shuffleQuestions: qs.config?.shuffleQuestions ?? false,
    },
    showMarks: qs.config?.showMarks ?? false,
    // Absent means derived, which is what 'auto' says in the picker.
    examInterface: qs.config?.examInterface ?? 'auto',
    working: qs.config?.working ?? 'default',
    // Absent means "follow the preset", and the preset is the only thing that
    // turns it on today — so an absent value on a test set reads as on.
    // Derive the two checkboxes from the asserted pyqType. Every stored set now
    // carries one (backfill 006), so there is no legacy fallback.
    isPYQ: qs.pyqType === 'FullPYP' || qs.pyqType === 'MixedPYP',
    isPyp: qs.pyqType === 'FullPYP',
    pyp: qs.pypRef
      ? {
          exam: qs.pypRef.exam,
          class: qs.pypRef.class,
          subjects: qs.pypRef.subjects,
          date: qs.pypRef.date,
          session: qs.pypRef.session ?? '',
          displayString: qs.pypRef.displayString,
        }
      : emptyForm().pyp,
    questionIds: qs.questionIds,
    sectionsEnabled: !!qs.sections?.length,
    sections: (qs.sections ?? []).map(s => ({
      id: s.id,
      title: s.title ?? '',
      size: s.questionIds.length,
      requiredCount: s.requiredCount ?? null,
      marking: s.marking,
    })),
    marking: qs.marking,
    aiInstructions: qs.config?.aiInstructions ?? {},
  };
}

export function Tests() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<QuestionSet | null>(null);
  const [viewing, setViewing] = useState<QuestionSet | null>(null);
  // Server-computed write authority for the set on screen — see TestDetailView.
  const [viewingCanEdit, setViewingCanEdit] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  /* What kind is being edited. Up here rather than in the render branch, because
     the effects below key off it as much as the markup does. */
  const isTopicExercise = form.purpose === 'topic-exercise';
  const isMockTest = !isTopicExercise && !form.isPyp;
  const isTopicwisePyq = isTopicExercise && form.isPYQ;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionPending, setActionPending] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const exporter = useTestQuestionsExport();

  // Modules for the topic-exercise anchor (shared TopicSelector builds the
  // module → chapter → topic tree from this flat per-subject list).
  const { data: modulesData } = useQuery({
    queryKey: ['modules'],
    queryFn: () => adminApi.getModules(),
  });
  const modulesBySubject = useMemo(() => {
    const grouped: Record<string, Module[]> = {};
    for (const mod of modulesData?.modules ?? []) {
      (grouped[mod.subject] ??= []).push(mod);
    }
    return grouped;
  }, [modulesData?.modules]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Belt to the detail view's braces: a public set is authored on dev and
      // carried here by export/import, so an edit made on prod would be
      // overwritten by the next import — and silently, since version no longer
      // counts as a diff. The Edit button is already disabled; this catches a
      // form left open across an environment switch.
      if (editing && editing.visibility === 'public' && !isDevEnvironment()) {
        throw new Error('Public tests are edited on dev and imported here. Make this change on dev.');
      }
      const isTopicExercise = form.purpose === 'topic-exercise';
      const isCustom = form.playMode === 'custom';
      const timeLimitSeconds = form.timeLimitMinutes ? Number(form.timeLimitMinutes) * 60 : undefined;
      // Preset-OR-Custom (§12). PRESET sets store a SPARSE config — the modality preset
      // supplies the knobs at play time (creator only sets a time limit). CUSTOM sets store
      // a FULL explicit config used verbatim (no allowedModalities). Legacy sets load as
      // Custom, so their full config round-trips on edit. See modality.ts.
      // AI guidance applies to the set regardless of play mode. Trim blanks so
      // empty slots persist as unset (prompt presence-conditionals don't render).
      const ai: AiInstructionsValue = {};
      for (const k of ['general', 'hint', 'analysis', 'learningMoments'] as const) {
        const v = form.aiInstructions[k]?.trim();
        if (v) ai[k] = v;
      }
      const aiPart = Object.keys(ai).length > 0 ? { aiInstructions: ai } : {};
      // showMarks is a display flag that rides the config snapshot in BOTH preset
      // and custom modes (orthogonal to the play knobs). Always sent explicitly so
      // turning it off overwrites a previously-stored true on update.
      const showMarksPart = { showMarks: form.showMarks };
      // Always sent, including 'auto' — which the server deletes rather than
      // stores, so a paper can be unpinned back to being derived. Sending
      // nothing would leave the previous pin in place, because config merges.
      const examInterfacePart = { examInterface: form.examInterface };
      // Same shape: 'default' is deleted by the server, clearing a stored value.
      const workingPart = { working: form.working };
      const presetConfig = { ...(timeLimitSeconds !== undefined ? { timeLimitSeconds } : {}), ...aiPart, ...showMarksPart, ...examInterfacePart, ...workingPart };
      const config = isCustom
        ? {
            defaultHintLevel: form.custom.defaultHintLevel,
            showFeedbackImmediately: form.custom.showFeedbackImmediately,
            shuffleQuestions: form.custom.shuffleQuestions,
            allowMultipleAttempts: form.custom.allowMultipleAttempts,
            allowAnalysisBeforeSubmit: form.custom.allowAnalysisBeforeSubmit,
            ...(timeLimitSeconds !== undefined ? { timeLimitSeconds } : {}),
            ...aiPart,
            ...showMarksPart,
            ...examInterfacePart,
            ...workingPart,
          }
        : presetConfig;
      // Preset sets carry allowedModalities + default; Custom sets carry neither.
      const modalityFields = isCustom
        ? {}
        : {
            defaultModality: form.playMode as QuestionSetModality,
            allowedModalities: (form.allowSwitch ? ['practice', 'test'] : [form.playMode]) as QuestionSetModality[],
          };
      const pypRef: ExamPaperRef | undefined = (!isTopicExercise && form.isPyp)
        ? {
            exam: form.pyp.exam,
            class: form.pyp.class,
            subjects: form.pyp.subjects,
            date: form.pyp.date,
            session: form.pyp.session || undefined,
            displayString: form.pyp.displayString || buildDisplayString(form.pyp),
          }
        : undefined;
      // Creator-asserted PYQ claim (§3f). isPyp ("complete paper") is only ever set for
      // exam-paper; topic exercises use the PYQ toggle (→ MixedPYP) without a full option.
      const pyqType: 'FullPYP' | 'MixedPYP' | 'None' = form.isPyp ? 'FullPYP' : form.isPYQ ? 'MixedPYP' : 'None';

      // Phase 2: sections are authoritative for questionIds when enabled.
      // On update, an empty array clears sections back to flat.
      const sections = buildSections(form);
      const difficulty = form.difficulty || undefined;

      if (editing) {
        // P1: anchor/modality/purpose are set at creation only (the update API
        // doesn't accept them yet); they're preserved untouched on edit.
        return questionSetApi.update({
          questionSetId: editing.id,
          title: form.title,
          description: form.description || undefined,
          config,
          status: editing ? editing.status : 'draft',
          targetExam: isTopicExercise ? undefined : (form.targetExam || undefined),
          // Topic exercises: send the exam array (empty clears to generic). Exam papers keep singular targetExam.
          ...(isTopicExercise ? { targetExams: form.targetExams } : {}),
          classes: form.classes.length > 0 ? form.classes : undefined,
          pyqType,
          ...(isTopicExercise ? { difficulty } : { pypRef }),
          source: form.source || undefined,
          // Flat sets: persist the picker's exact order (incl. Sort by exam) and
          // any add/remove in one shot. Sectioned sets derive order from sections.
          questionIds: form.sectionsEnabled ? undefined : form.questionIds,
          sections: form.sectionsEnabled ? (sections ?? []) : (editing.sections?.length ? [] : undefined),
          marking: form.marking ?? (editing.marking ? null : undefined),
        });
      }
      // Create
      const res = await questionSetApi.create({
        title: form.title,
        questionIds: form.questionIds,
        ...(sections ? { sections } : {}),
        ...(form.marking ? { marking: form.marking } : {}),
        visibility: 'public',
        description: form.description || undefined,
        config,
        status: 'draft',
        classes: form.classes.length > 0 ? form.classes : undefined,
        source: form.source || undefined,
        purpose: form.purpose,
        pyqType,
        // Preset sets carry modality fields; Custom sets omit them (config used verbatim). §12.
        ...modalityFields,
        ...(isTopicExercise
          ? {
              // type omitted: modality (not `type`) drives how a topic exercise plays.
              // The anchor IS the authored topic — one claim, one field.
              topics: form.anchorModuleId ? [form.anchorModuleId] : undefined,
              difficulty,
              targetExams: form.targetExams,
            }
          : {
              type: 'test',
              targetExam: form.targetExam || undefined,
              pypRef,
            }),
      });

      // Sync question picker selection to existing set (add/remove diff) if editing
      return res;
    },
    onSuccess: (result) => {
      // Flat sets persist their full ordered questionIds via the update call
      // above; sectioned sets derive questionIds from sections. Either way the
      // single update is authoritative — no separate add/remove diff needed.
      queryClient.invalidateQueries({ queryKey: ['admin-tests'] });

      // Land straight on the read-only detail view using the doc the save just
      // returned (update/create echo the full fresh questionSet). Keep this
      // handler SYNCHRONOUS: an async onSuccess keeps the mutation `pending` in
      // React Query v5, which intermittently flashed "Saving…" on the still-
      // mounted edit form while a post-save refetch was in flight. Swapping all
      // state in one batched render also avoids briefly showing the list.
      setForm(emptyForm());
      setSaveError(null);
      setEditing(null);
      setCreating(false);
      setViewing(result?.questionSet ?? null);
    },
    onError: (err) => setSaveError(err instanceof Error ? err.message : 'Save failed'),
  });

  const handleCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm(emptyForm());
    setSaveError(null);
  };

  // Row click opens the read-only detail view; the list is a summary projection,
  // so load the full doc first (also reused when the user clicks Edit there).
  const handleView = async (qs: QuestionSet) => {
    setCreating(false);
    setEditing(null);
    setSaveError(null);
    try {
      const { questionSet, canEdit } = await questionSetApi.get(qs.id);
      setViewing(questionSet);
      setViewingCanEdit(canEdit !== false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to load test');
    }
  };

  // Edit from the detail view — reuse the already-loaded full doc (no refetch).
  const handleEditFromView = () => {
    if (!viewing) return;
    setEditing(viewing);
    setForm(fromQuestionSet(viewing));
    setViewing(null);
    setSaveError(null);
  };

  // Toggle published/draft from the detail view. Status-only update (config is
  // sent unchanged because the update endpoint requires it); re-fetch so the
  // badge + button reflect the new status.
  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!viewing) return;
      const status = viewing.status === 'published' ? 'draft' : 'published';
      return questionSetApi.update({ questionSetId: viewing.id, status, config: viewing.config });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tests'] });
      if (viewing) {
        const { questionSet } = await questionSetApi.get(viewing.id);
        setViewing(questionSet);
      }
    },
    // Surface publish failures (e.g. the "all questions must be live" guard).
    onError: (err) => alert(err instanceof Error ? err.message : 'Failed to change publish status'),
  });

  const handleCancel = () => {
    setCreating(false);
    setEditing(null);
    setViewing(null);
    setForm(emptyForm());
    setSaveError(null);
  };

  const bulkAction = async (action: 'publish' | 'unpublish' | 'delete') => {
    const ids = [...selectedIds];
    const label = action === 'delete' ? `Delete ${ids.length} test(s)` : `${action === 'publish' ? 'Publish' : 'Unpublish'} ${ids.length} test(s)`;
    if (!confirm(`${label}? This cannot be undone.`)) return;
    setActionPending(true);
    try {
      for (const id of ids) {
        if (action === 'delete') {
          await questionSetApi.remove(id);
        } else {
          await questionSetApi.update({ questionSetId: id, status: action === 'publish' ? 'published' : 'draft' });
        }
      }
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['admin-tests'] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionPending(false);
    }
  };

  // Export the questions of the selected test(s) as a re-importable bundle.
  // Gathers questionIds across the selection (deduped by the hook), then hands
  // off to the existing /console/questions/export-by-ids endpoint.
  const exportSelectedQuestions = async () => {
    setActionPending(true);
    try {
      // Fetch full docs for just the selection — the list endpoint returns a
      // lightweight summary projection that omits questionIds.
      const { questionSets } = await questionSetApi.exportByIds([...selectedIds]);
      const ids = questionSets.flatMap(qs => (qs.questionIds as string[] | undefined) ?? []);
      const title0 = questionSets[0]?.title as string | undefined;
      const filenameBase = questionSets.length === 1 && title0 ? slugify(title0) : `${questionSets.length}-tests`;
      exporter.requestExport(ids, filenameBase);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to load selected tests');
    } finally {
      setActionPending(false);
    }
  };

  // Export the selected test DOCS (not their questions) as a bundle for import
  // into another environment via the "Import tests" dialog.
  const exportSelectedTests = async () => {
    setActionPending(true);
    try {
      const { questionSets } = await questionSetApi.exportByIds([...selectedIds]);
      downloadJson({ questionSets }, `tests-${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to export tests');
    } finally {
      setActionPending(false);
    }
  };

  // Shared export UI (choice dialog + progress + result toast + warnings),
  // rendered in both the list and form views.
  const exportUi = (
    <>
      <Modal isOpen={exporter.isPickerOpen} onClose={exporter.cancelExport} title="Export Questions" size="sm">
        <div className="export-dialog-options">
          <button className="export-dialog-option" onClick={() => exporter.runExport(false)}>
            Export with URLs only
            <div className="subtitle">Smaller file, faster export</div>
          </button>
          <button className="export-dialog-option" onClick={() => exporter.runExport(true)}>
            Export with embedded images
            <div className="subtitle">Larger file, portable — recommended for import to another environment</div>
          </button>
        </div>
      </Modal>

      {exporter.exportStep && <div className="test-export-progress">{exporter.exportStep}</div>}

      {exporter.status && (
        <div className={`test-export-toast test-export-toast-${exporter.status.type}`} onClick={exporter.clearStatus}>
          {exporter.status.message}
        </div>
      )}

      <Modal isOpen={!!exporter.warnings} onClose={exporter.clearWarnings} title="Export Warnings" size="sm">
        <div className="test-export-warnings">
          {(exporter.warnings ?? []).map((w, i) => (
            <div key={i} className="test-export-warning">{w}</div>
          ))}
        </div>
      </Modal>
    </>
  );

  // displayString is always derived from the structured PYP fields (not editable).
  useEffect(() => {
    if (!form.isPyp) return;
    const next = buildDisplayString(form.pyp);
    if (next !== form.pyp.displayString) {
      setForm(prev => ({ ...prev, pyp: { ...prev.pyp, displayString: next } }));
    }
  }, [form.isPyp, form.pyp]);

  /**
   * A paper's Class and Exam ARE its targeting — for a mock as much as a real one.
   *
   * Both now ask them once, in Paper Details, and the standalone Target classes
   * and Target exam controls are gone for these kinds. So this has to track the
   * fields rather than seed them: the old version filled `classes` only while it
   * was empty, which was right when a human could still correct it afterwards
   * and wrong now that nothing else can. Changing the Class must move the set.
   */
  const isPaperKind = !isTopicExercise && (form.isPyp || isMockTest);
  useEffect(() => {
    if (!isPaperKind) return;
    setForm(prev => {
      const classes = prev.pyp.class ? [prev.pyp.class] : prev.classes;
      const targetExam = prev.pyp.exam || prev.targetExam;
      if (classes === prev.classes && targetExam === prev.targetExam) return prev;
      return { ...prev, classes, targetExam };
    });
  }, [isPaperKind, form.pyp.class, form.pyp.exam]);

  const isMultiSubjectExam = form.pyp.exam === 'JEE' || form.pyp.exam === 'NEET';

  // JEE/NEET are class-12 exams — lock the class to '12' (the field is also
  // disabled). Runs on edit-load too, not just when the exam is changed.
  useEffect(() => {
    if (isMultiSubjectExam && form.pyp.class !== '12') {
      setForm(p => ({ ...p, pyp: { ...p.pyp, class: '12' } }));
    }
  }, [isMultiSubjectExam, form.pyp.class]);

  // A previous-year paper can't be dated in the future — cap the date picker.
  const today = new Date().toISOString().slice(0, 10);

  // Class 10 and below have a combined "science" subject (physics/chemistry/
  // biology split only from class 11); offer it as an extra option there.
  const isLowerClass = ['7', '8', '9', '10'].includes(form.pyp.class);
  const pypSubjectOptions = isLowerClass ? [...SUBJECT_OPTIONS, 'science'] : [...SUBJECT_OPTIONS];

  // Drop a stale "science" selection if the class moves to 11/12.
  useEffect(() => {
    if (isLowerClass || !form.pyp.subjects.includes('science')) return;
    setForm(prev => ({ ...prev, pyp: { ...prev.pyp, subjects: prev.pyp.subjects.filter(s => s !== 'science') } }));
  }, [isLowerClass, form.pyp.subjects]);

  const togglePypSubject = (s: string) => {
    setForm(prev => {
      if (!isMultiSubjectExam) {
        // Single-select: replace current selection
        return { ...prev, pyp: { ...prev.pyp, subjects: [s] } };
      }
      const has = prev.pyp.subjects.includes(s);
      return {
        ...prev,
        pyp: {
          ...prev.pyp,
          subjects: has ? prev.pyp.subjects.filter(x => x !== s) : [...prev.pyp.subjects, s],
        },
      };
    });
  };

  // ── Detail (read-only) view ──────────────────────────────────────────
  if (viewing) {
    return (
      <>
        <TestDetailView
          qs={viewing}
          canEdit={viewingCanEdit}
          onEdit={handleEditFromView}
          onTogglePublish={() => publishMutation.mutate()}
          publishing={publishMutation.isPending}
          onBack={handleCancel}
        />
        {exportUi}
      </>
    );
  }

  // ── Form view ────────────────────────────────────────────────────────
  if (creating || editing) {
    /* Read back through the same function the library reads a stored set with,
       so the card that lights up is the shelf this paper will land on. */
    const selectedKind = kindOf({
      purpose: form.purpose,
      pyqType: form.isPyp ? 'FullPYP' : form.isPYQ ? 'MixedPYP' : 'None',
    });
    // Seed the question picker's module filter from the Topic Exercise anchor
    // (editable afterwards). The picker's TopicSelector selects by plain module id
    // (module/chapter/topic — selecting a module pulls everything beneath it).
    const pickerModuleSeed = form.anchorModuleId;
    // Modules for the anchor's TopicSelector (module → chapter → topic), mapped to its
    // ModuleItem shape (fills the fields ModuleItem requires but admin Module leaves optional).
    const anchorModules = (modulesBySubject[form.anchorSubject] ?? []).map(m => ({
      id: m.id,
      displayName: m.displayName,
      subject: m.subject,
      isTopModule: m.isTopModule ?? (m.moduleLevel === 'module'),
      moduleLevel: m.moduleLevel ?? 'chapter',
      parentModuleId: m.parentModuleId,
      sortOrder: m.sortOrder ?? 0,
      grades: m.grades,
    }));
    const missingFields: string[] = [];
    if (!form.title.trim()) missingFields.push('Title');
    if (form.questionIds.length === 0) missingFields.push('Questions');
    if (isTopicExercise && creating && !form.anchorModuleId) missingFields.push('Anchor topic/chapter');
    // Everything authored here is a public test, and student discovery filters
    // on class and exam: a set naming neither is invisible in the Test Library
    // with nothing on screen to explain why. A PYP carries both on its paper
    // reference, so it satisfies these through the PYP fields below.
    // Named as the control the reader can actually see: a mock and a topicwise
    // PYQ ask for these as "Class" and "Exam" inside their details box, and
    // "Target classes" now names nothing on their screen.
    if (!form.isPyp && form.classes.length === 0) {
      missingFields.push(isMockTest || isTopicwisePyq ? 'Class' : 'Target classes');
    }
    // Topic exercises are reached by their anchor topic, not by exam, and the
    // save path deliberately sends no targetExam for them.
    if (!form.isPyp && !isTopicExercise && !form.targetExam) missingFields.push('Exam');
    if (isTopicwisePyq && form.targetExams.length === 0) missingFields.push('Exam');
    // Play mode (preset or custom) is always valid — no extra checks needed.
    if (!isTopicExercise && form.isPyp) {
      if (!form.pyp.exam) missingFields.push('Exam');
      if (!form.pyp.class) missingFields.push('Class');
      if (form.pyp.subjects.length === 0) missingFields.push('Subjects');
      if (!form.pyp.date) missingFields.push('Date');
    }
    const sectionErrors = validateSectionForm(form);
    const canSave = missingFields.length === 0 && sectionErrors.length === 0;

    // ── Section editor handlers (Phase 2) ──
    const updateSection = (i: number, patch: Partial<SectionForm>) =>
      setForm(p => ({ ...p, sections: p.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
    const addSection = () => setForm(p => {
      const used = p.sections.reduce((sum, s) => sum + s.size, 0);
      const remaining = Math.max(1, p.questionIds.length - used);
      return { ...p, sections: [...p.sections, { id: crypto.randomUUID(), title: '', size: remaining, requiredCount: null }] };
    });
    const removeSection = (i: number) => setForm(p => ({ ...p, sections: p.sections.filter((_, idx) => idx !== i) }));
    const toggleSections = (on: boolean) => setForm(p => ({
      ...p,
      sectionsEnabled: on,
      // Seed one section spanning all questions when first enabling.
      sections: on && p.sections.length === 0
        ? [{ id: crypto.randomUUID(), title: 'Section A', size: p.questionIds.length, requiredCount: null }]
        : p.sections,
    }));

    return (
      <div className="admin-tests-page">
        <BackButton label="list" onClick={handleCancel} />
        <h1>{editing ? 'Edit' : 'Create'} {isTopicExercise ? 'Topic Exercise' : 'Test'}</h1>

        <div className="test-form">
          <div className="test-form-field">
            <label>Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. CBSE Class 10 Maths Board Paper 2024"
            />
          </div>

          {/* What this paper IS — the first decision, and the one every section
              below follows from. It used to be asked as a type radio plus one or
              two PYQ checkboxes, which is the same four answers spelled as three
              controls: a reader had to know that "Exam / Test" plus both boxes
              means a past paper. The four the library actually shelves are named
              here instead, and `KIND_FORM` sets the dimensions underneath. */}
          <div className="test-form-field">
            <label>What is this? *</label>
            <div className="test-kind-row">
              {QUESTION_SET_KINDS.map(k => (
                <label
                  key={k}
                  className={`test-kind-card${selectedKind === k ? ' test-kind-card--on' : ''}`}
                >
                  <input
                    type="radio"
                    name="question-set-kind"
                    checked={selectedKind === k}
                    disabled={!!editing}
                    onChange={() => setForm(p => {
                      const next = KIND_FORM[k];
                      return {
                        ...p,
                        purpose: next.purpose,
                        isPYQ: next.isPYQ,
                        isPyp: next.isPyp,
                        playMode: next.playMode,
                        allowSwitch: next.purpose === 'topic-exercise',
                        custom: { ...MODE_PRESET[next.playMode] },
                      };
                    })}
                  />
                  <span className="test-kind-name">{QUESTION_SET_KIND_LABELS[k]}</span>
                  <span className="test-kind-hint">{KIND_HINT[k]}</span>
                </label>
              ))}
            </div>
            {editing && <span className="test-hint">Kind, anchor and play modes are fixed after creation.</span>}
          </div>

          {/**
           * What this set IS — one box, whichever kind was picked.
           *
           * Two fieldsets used to sit here, one per family, and the same
           * question was asked in a different place and a different order in
           * each. They are the same questions: who it is for, what it covers,
           * how long it is sat. So one block, one order, and the kind decides
           * only which of the rows apply.
           *
           *   Row 1  who it is for       Exam · Class · Subject
           *   Row 2  what it covers      Topic · Difficulty  |  Date · Session
           *   Row 3  how it is sat       Time limit
           *
           * Three to a row at most: these are short selects, and a fourth made
           * the row read as a list of unrelated settings.
           */}
          <fieldset className="test-form-pyp">
            <legend>{isTopicExercise ? 'Topic Exercise' : 'Paper Details'}</legend>

            <div className="test-form-row">
              <div className="test-form-field">
                <label>{isTopicExercise ? 'Target exam' : 'Exam *'}</label>
                <select
                  value={isTopicExercise ? (form.targetExams[0] ?? '') : form.pyp.exam}
                  onChange={e => {
                    const exam = e.target.value;
                    if (isTopicExercise) {
                      setForm(p => ({ ...p, targetExams: exam ? [exam] : [] }));
                      return;
                    }
                    // JEE and NEET are class-12 and multi-subject; switching to
                    // one fixes the class and leaves room for more than one
                    // subject, switching away trims back to a single.
                    const isMulti = exam === 'JEE' || exam === 'NEET';
                    setForm(p => ({
                      ...p,
                      pyp: {
                        ...p.pyp,
                        exam,
                        class: isMulti ? '12' : p.pyp.class,
                        subjects: !isMulti && p.pyp.subjects.length > 1 ? [p.pyp.subjects[0]] : p.pyp.subjects,
                      },
                    }));
                  }}
                >
                  <option value="">{isTopicExercise ? '— any exam —' : '—'}</option>
                  {EXAM_OPTIONS.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>

              <div className="test-form-field">
                <label>Class *</label>
                <select
                  value={isTopicExercise ? (form.classes[0] ?? '') : form.pyp.class}
                  onChange={e => {
                    const cls = e.target.value;
                    setForm(p => (isTopicExercise
                      ? { ...p, classes: cls ? [cls] : [] }
                      : { ...p, pyp: { ...p.pyp, class: cls } }));
                  }}
                  disabled={!isTopicExercise && isMultiSubjectExam}
                >
                  <option value="">—</option>
                  {GRADE_OPTIONS.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>

              {/* One subject anchors an exercise and drives its module picker;
                  a paper can cover several. A mock asks for neither — its
                  subjects are derived from the questions picked below. */}
              {isTopicExercise && (
                <div className="test-form-field">
                  <label>Subject *</label>
                  <select
                    value={form.anchorSubject}
                    disabled={!!editing}
                    onChange={e => setForm(p => ({ ...p, anchorSubject: e.target.value, anchorModuleId: '' }))}
                  >
                    <option value="">— select —</option>
                    {QUESTION_SUBJECT_OPTIONS.map(s => <option key={s} value={s}>{capitalize(s)}</option>)}
                  </select>
                </div>
              )}
              {form.isPyp && (
                <div className="test-form-field">
                  <label>Subjects * <InfoTip text="Single-subject papers (CBSE) or multi-subject (JEE/NEET: PCM/PCB)" /></label>
                  <div className="test-checkbox-row">
                    {pypSubjectOptions.map(s => (
                      <label key={s} className="check-label">
                        <input
                          type="checkbox"
                          checked={form.pyp.subjects.includes(s)}
                          onChange={() => togglePypSubject(s)}
                        />
                        <span>{capitalize(s)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="test-form-row">
              {isTopicExercise && (<>
                <div className="test-form-field">
                  <label>Topic / chapter * <InfoTip text="The module, chapter or topic this exercise is anchored to. Search to filter. Set at creation." /></label>
                  <TopicSelector
                    modules={anchorModules}
                    value={form.anchorModuleId}
                    onChange={v => setForm(p => ({ ...p, anchorModuleId: v }))}
                    placeholder={form.anchorSubject ? 'Search modules / chapters / topics…' : 'Select a subject first'}
                    disabled={!!editing || !form.anchorSubject}
                  />
                </div>
                <div className="test-form-field">
                  <label>Difficulty</label>
                  <select
                    value={form.difficulty}
                    onChange={e => setForm(p => ({ ...p, difficulty: e.target.value as FormState['difficulty'] }))}
                  >
                    <option value="">—</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </>)}

              {/* A sitting's own facts. A mock was never sat, so it has neither. */}
              {form.isPyp && (<>
                <div className="test-form-field">
                  <label>Date *</label>
                  <DatePicker
                    value={form.pyp.date}
                    max={today}
                    ariaLabel="Date"
                    onChange={v => { if (!v || v <= today) setForm(p => ({ ...p, pyp: { ...p.pyp, date: v } })); }}
                  />
                </div>
                <div className="test-form-field">
                  <label>Session / Set</label>
                  <input
                    type="text"
                    maxLength={16}
                    value={form.pyp.session}
                    onChange={e => setForm(p => ({ ...p, pyp: { ...p.pyp, session: e.target.value } }))}
                    placeholder="Shift 1, Set 56/2/1"
                  />
                </div>
              </>)}

              <div className="test-form-field">
                <label>Time limit (minutes) <InfoTip text="Used in Test mode. Practice mode is always untimed." /></label>
                <input
                  type="number"
                  min={1}
                  value={form.timeLimitMinutes}
                  onChange={e => setForm(p => ({ ...p, timeLimitMinutes: e.target.value }))}
                  placeholder="blank = no limit"
                />
              </div>

              {/* Which screen a TEST sitting of this paper runs on — a fact about
                  how it is sat, so it belongs beside the clock. Automatic is the
                  answer for almost every paper: a JEE past paper, mock or
                  topicwise set already resolves the JEE Main screen with nobody
                  choosing. The other three exist for the cases derivation cannot
                  reach — JEE Advanced, which is indistinguishable from Main in
                  the data, and a JEE-tagged paper that should not be sat as an
                  exam at all. */}
              <div className="test-form-field">
                <label>
                  Exam screen
                  <InfoTip text="Replicates the real exam's computer-based-test screen — question palette, mark for review, on-screen keypad. Test mode only; practice always uses the app's own screen." />
                </label>
                <select
                  value={form.examInterface}
                  onChange={e => setForm(p => ({ ...p, examInterface: e.target.value as typeof p.examInterface }))}
                >
                  <option value="auto">Automatic (JEE papers get JEE Main)</option>
                  <option value="jee-main">JEE Main</option>
                  <option value="jee-advanced">JEE Advanced</option>
                  <option value="none">None — the app's own screen</option>
                </select>
              </div>

              <div className="test-form-field">
                <label>
                  Working
                  <InfoTip text={WORKING_HELP} />
                </label>
                <select
                  value={form.working}
                  onChange={e => setForm(p => ({ ...p, working: e.target.value as WorkingChoice }))}
                >
                  {WORKING_CHOICES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
          </fieldset>

          <div className="test-form-field">
            <label>Questions * <span className="test-hint">{form.questionIds.length} selected</span></label>
            <QuestionPicker
              selectedIds={form.questionIds}
              onSelectionChange={ids => setForm(p => ({ ...p, questionIds: ids }))}
              {...(isTopicExercise ? {
                initialSubject: form.anchorSubject || undefined,
                initialModuleId: pickerModuleSeed || undefined,
                initialDifficulty: form.difficulty || undefined,
              } : {})}
            />
          </div>

          {/* Tier 2: test-level marking scheme (sections may override). Worth
              comes from each question's marks. See docs/FeatureDesigns/AssessmentAndMarking/scoring-and-marking.md. */}
          <div className="test-form-field">
            <label>
              Marking{' '}
              <InfoTip>
                <strong>How marks are awarded</strong>
                <span>Each question is worth its own <b>marks</b>; the scheme decides how those marks are awarded.</span>
                <span><b>No marking</b> — marks aren’t awarded; no marks shown in results.</span>
                {Object.values(MARKING_PRESETS).map(p => (
                  <span key={p.label}><b>{p.label}</b> — {p.description}</span>
                ))}
                <span className="info-tip-note">Subjective (freeform / structured) questions are always graded by rubric — proportional to the AI’s score — whatever the scheme. Sections can override the test default.</span>
              </InfoTip>
            </label>
            <MarkingSelect value={form.marking} onChange={m => setForm(p => ({ ...p, marking: m }))} />
          </div>

          {/* Phase 2: sections (attempt any K of N). Questions are partitioned in
              order by each section's size. See question-structure.md §Phase 2. */}
          <div className="test-form-field test-sections">
            <label className="test-sections-toggle">
              <input type="checkbox" checked={form.sectionsEnabled} onChange={e => toggleSections(e.target.checked)} />
              <span>Use sections</span>
              <InfoTip text="Split the test into sections (e.g. JEE Section A / B). A section can let students attempt any K of N questions — the best K are scored. Questions fill sections in order." />
            </label>
            {form.sectionsEnabled && (
              <div className="test-sections-editor">
                {form.sections.map((s, i) => {
                  const offset = form.sections.slice(0, i).reduce((sum, x) => sum + x.size, 0);
                  return (
                    <div key={s.id} className="test-section-row">
                      <span className="test-section-tag">{i + 1}</span>
                      <input
                        className="test-section-title"
                        placeholder={`Section ${String.fromCharCode(65 + i)}`}
                        value={s.title}
                        onChange={e => updateSection(i, { title: e.target.value })}
                      />
                      <label className="test-section-num">
                        Questions
                        <IntegerInput min={1} value={s.size}
                          onChange={v => updateSection(i, { size: v ?? 1 })} />
                      </label>
                      <label className="test-section-num">
                        Attempt any
                        <IntegerInput min={1} max={s.size} allowEmpty
                          placeholder={`${s.size}`}
                          value={s.requiredCount}
                          onChange={v => updateSection(i, { requiredCount: v })} />
                        <span className="test-section-of">of {s.size}</span>
                      </label>
                      <span className="test-section-range">Q{offset + 1}–{offset + s.size}</span>
                      <label className="test-section-num">
                        Marking
                        <MarkingSelect value={s.marking} onChange={m => updateSection(i, { marking: m })} allowInherit />
                      </label>
                      <button className="test-section-remove" onClick={() => removeSection(i)} title="Remove section">×</button>
                    </div>
                  );
                })}
                <button className="btn-secondary test-section-add" onClick={addSection}>+ Add Section</button>
                {sectionErrors.length > 0 && (
                  <ul className="test-section-errors">
                    {sectionErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>

          {saveError && <div className="test-error">{saveError}</div>}

          <div className="test-form-actions">
            <button
              className="btn-secondary"
              onClick={() => exporter.requestExport(form.questionIds, slugify(form.title))}
              disabled={form.questionIds.length === 0 || exporter.isExporting}
              title="Download this test's questions as a re-importable JSON bundle"
            >
              {exporter.isExporting ? 'Exporting…' : 'Export questions'}
            </button>
            <span className="test-form-actions-spacer" />
            <button className="btn-secondary" onClick={handleCancel}>Cancel</button>
            <button
              className="btn-primary"
              onClick={() => canSave ? saveMutation.mutate() : alert(`Missing required fields: ${missingFields.join(', ')}`)}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : (editing ? 'Save' : 'Create')}
            </button>
          </div>
        </div>
        {exportUi}
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────
  // The admin list reuses @shared-ui/test-library for rendering/filters/selection,
  // and layers admin-only controls (status filter, bulk publish/unpublish/delete) on top.
  const handleToggle = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleToggleAll = (visibleIds: string[]) => {
    setSelectedIds(prev => {
      const allChecked = visibleIds.every(id => prev.has(id));
      if (allChecked) {
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of visibleIds) next.add(id);
      return next;
    });
  };

  return (
    <div className="admin-tests-page">
      <div className="test-header">
        <h1>Question Sets</h1>
        {/* Public tests are authored on dev and carried to prod by export/import,
            so prod is read-only for them — hide the creation entry points there. */}
        {isDevEnvironment() && (
          <div className="test-header-actions">
            <button className="btn-secondary" onClick={() => setShowImport(true)}>Import tests</button>
            <button className="btn-primary" onClick={handleCreate}>+ Create Test</button>
          </div>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div className="test-action-bar">
          <span>{selectedIds.size} selected</span>
          <button className="btn-secondary" onClick={() => bulkAction('publish')} disabled={actionPending}>Publish</button>
          <button className="btn-secondary" onClick={() => bulkAction('unpublish')} disabled={actionPending}>Unpublish</button>
          <button className="btn-secondary" onClick={exportSelectedTests} disabled={actionPending}>Export tests</button>
          <button className="btn-secondary" onClick={exportSelectedQuestions} disabled={actionPending || exporter.isExporting}>Export questions</button>
          <button className="btn-danger" onClick={() => bulkAction('delete')} disabled={actionPending}>Delete</button>
          <button className="btn-secondary" onClick={() => setSelectedIds(new Set())} disabled={actionPending}>Clear</button>
        </div>
      )}

      <TestLibrary
        fetchTests={(filters, page, sort) =>
          questionSetApi.list({
            // NO `type` here, deliberately. The server reads that legacy content
            // label as the PLAY axis (`c.defaultModality = 'test'`), and a topic
            // exercise is authored through the Tests route and plays as practice
            // — so asking for 'test' emptied both Topicwise PYQ tabs of all 24
            // sets they hold. The tab's own kind already narrows by purpose and
            // pyqType, which is the axis that actually means "what is this".
            // Same fault, same fix as `listPublicTests`; see test-modality-target.md.
            creatorOnly: true,
            ...testLibraryQueryParams(filters),
            page,
            pageSize: DEFAULT_TEST_LIBRARY_PAGE_SIZE,
            sortBy: sort?.by,
            sortDir: sort?.dir,
          }).then(r => ({ questionSets: r.questionSets, total: r.total ?? 0 }))
        }
        queryKey={['admin-tests']}
        storageKey="admin-tests-library"
        onRowClick={handleView}
        classOptions={[...GRADE_OPTIONS]}
        subjectOptions={[...QUESTION_SUBJECT_OPTIONS]}
        showStatusFilter
        purposeTabs
        showTopicExerciseFilter
        selection={{ selectedIds, onToggle: handleToggle, onToggleAll: handleToggleAll }}
        leadColumns={[
          {
            key: 'classes',
            header: 'Class',
            render: qs => qs.classes?.join(', ') ?? qs.pypRef?.class ?? '—',
          },
        ]}
        extraColumns={[
          {
            key: 'status',
            header: 'Status',
            render: qs => <span className={`test-status test-status-${qs.status}`}>{qs.status}</span>,
          },
          {
            key: 'attempts',
            header: 'Attempts',
            sortKey: 'attempts',
            render: qs => qs.timesAttempted,
          },
          {
            key: 'updated',
            header: 'Updated',
            sortKey: 'updated',
            tdClassName: 'test-date',
            // Short date + time — the sort is by the full updatedAt timestamp,
            // so showing the time makes same-day ordering visible.
            render: qs => new Date(qs.updatedAt).toLocaleString(undefined, {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
            }),
          },
        ]}
        emptyMessage="No tests yet. Create one to get started."
      />
      {exportUi}
      <TestImportDialog
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => queryClient.invalidateQueries({ queryKey: ['admin-tests'] })}
      />
    </div>
  );
}
